import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, LockKey, ShieldCheck, Eye, EyeSlash, UserPlus, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail } from "../lib/api";
import { apiClient } from "../lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [booted, setBooted] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setBooted(true), 1700);
    return () => clearTimeout(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back.");
        nav("/admin");
      } else {
        await apiClient.post("/auth/register", { name, email, password });
        setPendingApproval(true);
        toast.success("Account created — pending admin approval.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setPendingApproval(false);
    setPassword("");
  };

  const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <main className="relative min-h-screen overflow-hidden bg-brandblue-ink text-white flex items-center justify-center px-4" data-testid="admin-login-screen">
      {/* Grid background */}
      <div aria-hidden className="absolute inset-0 grid-bg opacity-70" />
      {/* Ambient orbs */}
      <div aria-hidden className="orb h-[520px] w-[520px] bg-brandblue/40 -top-20 -left-20" />
      <div aria-hidden className="orb h-[420px] w-[420px] bg-ova/30 bottom-0 right-0" />
      {/* Scan line */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-brandblue to-transparent scan-line" />

      {/* Boot splash */}
      <AnimatePresence>
        {!booted && (
          <motion.div
            key="boot"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 z-30 bg-brandblue-ink flex flex-col items-center justify-center"
          >
            <motion.img
              src="/logo.png"
              alt="MedexOVA"
              className="h-24 w-24 logo-glow"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: [0.8, 1.05, 1], opacity: 1 }}
              transition={{ duration: 1.2, times: [0, 0.6, 1] }}
            />
            <motion.div
              initial={{ opacity: 0, letterSpacing: "0.4em" }}
              animate={{ opacity: 1, letterSpacing: "0.2em" }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-6 text-xs uppercase font-bold text-brandblue"
            >
              MedexOVA Operations
            </motion.div>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 220 }}
              transition={{ duration: 1.1, delay: 0.4 }}
              className="mt-6 h-[3px] bg-gradient-to-r from-brandblue via-ova to-forest rounded-full"
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="mt-5 text-[11px] font-mono tracking-widest text-white/50"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              → establishing secure channel…
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={booted ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-20 w-full max-w-md"
      >
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-8 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)]">
          <div className="flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2" data-testid="admin-login-home-link">
              <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent overflow-hidden">
                <img src="/logo.png" alt="MedexOVA" className="h-8 w-8 object-contain logo-glow" />
              </span>
              <span className="font-heading text-lg tracking-tight font-black">
              <span className="text-[#16A34A]">Medex</span><span className="text-[#C4B5FD]">OVA</span>
              </span>
            </Link>
            <span
              className="text-[10px] font-mono text-white/40"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              {now}
            </span>
          </div>

          <div className="mt-7 inline-flex items-center gap-2 rounded-full border border-brandblue/30 bg-brandblue/10 px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold text-brandblue">
            <LockKey size={12} weight="bold" /> Secure sign-in
          </div>

          <div className="mt-5 flex gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-1" data-testid="admin-auth-tabs">
            <button
              type="button"
              onClick={() => switchMode("login")}
              data-testid="admin-auth-tab-login"
              className={`flex-1 rounded-md py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                mode === "login" ? "bg-brandblue text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              Log In
            </button>
            <button
              type="button"
              onClick={() => switchMode("register")}
              data-testid="admin-auth-tab-register"
              className={`flex-1 rounded-md py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                mode === "register" ? "bg-brandblue text-white" : "text-white/50 hover:text-white/80"
              }`}
            >
              Create New User
            </button>
          </div>

          {pendingApproval ? (
            <div className="mt-8 text-center" data-testid="admin-register-pending">
              <Clock size={40} weight="duotone" className="mx-auto text-brandblue" />
              <h2 className="mt-4 font-heading text-2xl font-bold">Pending admin approval</h2>
              <p className="mt-2 text-sm text-white/60">
                Your account was created. An admin was texted to approve access — you'll get an email
                the moment a decision is made, then you can log in.
              </p>
              <button
                type="button"
                onClick={() => switchMode("login")}
                data-testid="admin-register-pending-back-btn"
                className="mt-6 text-xs font-bold uppercase tracking-wider text-brandblue hover:text-brandblue-deep"
              >
                Back to sign-in
              </button>
            </div>
          ) : forgotMode ? (
            <div className="mt-8" data-testid="admin-forgot-password-panel">
              {forgotSent ? (
                <div className="text-center">
                  <ArrowRight size={0} />
                  <h2 className="font-heading text-2xl font-bold">Check your email</h2>
                  <p className="mt-2 text-sm text-white/60">
                    If {email || "that email"} has an admin account, a reset link is on its way — it expires in 1 hour.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setForgotMode(false); setForgotSent(false); }}
                    data-testid="admin-forgot-back-to-login-btn"
                    className="mt-6 text-xs font-bold uppercase tracking-wider text-brandblue hover:text-brandblue-deep"
                  >
                    Back to sign-in
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="font-heading text-2xl font-bold">Reset your password</h2>
                  <p className="mt-2 text-sm text-white/60">Enter your admin email and we'll send you a reset link.</p>
                  <form onSubmit={submitForgot} className="mt-6 space-y-4" data-testid="admin-forgot-password-form">
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Email</label>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                        placeholder="admin@medexova.com"
                        data-testid="admin-forgot-email-input"
                        autoFocus
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="admin-forgot-submit-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brandblue to-ova hover:from-brandblue-deep hover:to-ova-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-all"
                    >
                      {submitting ? "Sending…" : "Send reset link"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForgotMode(false)}
                      data-testid="admin-forgot-cancel-btn"
                      className="w-full text-center text-xs text-white/50 hover:text-white/80"
                    >
                      Back to sign-in
                    </button>
                  </form>
                </>
              )}
            </div>
          ) : (
            <>
              <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold leading-tight">
                {mode === "login" ? "Operations console." : "Request access."}
              </h2>
              <p className="mt-2 text-sm text-white/60">
                {mode === "login"
                  ? "Dispatch-grade visibility for every trip."
                  : "New accounts need a quick admin approval before first login."}
              </p>

              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
                {mode === "register" && (
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Full name</label>
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                      placeholder="Jordan Lee"
                      data-testid="admin-register-name-input"
                      autoFocus
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                    placeholder="admin@medexova.com"
                    data-testid="admin-email-input"
                    autoFocus={mode === "login"}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-bold text-white/60 mb-2">Password</label>
                  <div className="relative">
                    <input
                      type={showPw ? "text" : "password"}
                      required
                      minLength={mode === "register" ? 8 : undefined}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 pr-11 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-brandblue/40 focus:border-brandblue transition"
                      placeholder="••••••••"
                      data-testid="admin-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center text-white/50 hover:text-white"
                      data-testid="admin-password-toggle"
                      aria-label="Toggle password visibility"
                    >
                      {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setForgotSent(false); }}
                  className="text-[11px] text-white/50 hover:text-white/80 underline -mt-1 self-start"
                  data-testid="admin-forgot-password-link"
                >
                  Forgot password?
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={mode === "login" ? "admin-login-submit-btn" : "admin-register-submit-btn"}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-brandblue to-ova hover:from-brandblue-deep hover:to-ova-hover disabled:opacity-60 text-white px-6 py-3 text-sm font-semibold transition-all shadow-[0_10px_30px_-10px_rgba(124,58,237,0.6)]"
                >
                  {submitting
                    ? mode === "login" ? "Signing in…" : "Creating account…"
                    : mode === "login" ? "Unlock dashboard" : "Request account"}
                  {mode === "login" ? <ArrowRight weight="bold" size={14} /> : <UserPlus weight="bold" size={14} />}
                </button>
              </form>
            </>
          )}

          <div className="mt-7 flex items-center justify-between text-[11px] text-white/40">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck size={12} className="text-forest" /> HIPAA
            </span>
            <span
              className="font-mono"
              style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-forest inline-block mr-1.5 live-dot"></span>
              session encrypted
            </span>
          </div>
        </div>
        <p className="mt-5 text-center text-[11px] text-white/40">
          Authorized personnel only. Contact the MedexOVA administrator at info@medexova.com.
        </p>
      </motion.div>
    </main>
  );
}
